import React, { useEffect } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { getFullName } from "../../../../Modules/utils/fullname";
import { formatTypeLabel } from "./SelectTransactionType";

/**
 * SummaryCardLayout
 *
 * mode:       "preview" | "submitted" | "details"
 * data:       transaction data (either state OR backend response)
 * renderPreviewContent(data)
 * renderPrintableContent(data)
 *
 * Handlers:   onBack, onSubmit, onClose, onEdit, onDelete
 */

export default function SummaryCardLayout({
  mode,
  data,
  renderPreviewContent,
  renderPrintableContent,
  renderSummaryContent,
  onBack,
  onSubmit,
  onClose,
  onEdit,
  onDelete,
  onApprove,
}) {
  const componentRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);

  const printable = renderPrintableContent?.(data);
  const summaryContent = renderSummaryContent
    ? renderSummaryContent(data)
    : printable;

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  // HEADER — standard office + transaction type + date
  const renderHeader = () => {
    const officeName = getFullName(data.office) || "Office";
    const typeLabel = formatTypeLabel(data.transactionType);

    return (
      <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
        <div>
          <div className="fw-bold">{officeName}</div>
          <div className="small text-muted">{typeLabel}</div>
        </div>

        <div className="text-end small">
          <div className="text-muted">Date:</div>
          <div className="fw-semibold">{fmtDate(data.date)}</div>
        </div>
      </div>
    );
  };

  // FOOTERS
  const printButton = (
    <ReactToPrint
      content={() => componentRef.current}
      trigger={() => <Button variant="primary">Print</Button>}
    />
  );

  const PreviewFooter = (
    <div className="d-flex justify-content-between mt-3">
      <Button variant="outline-secondary" onClick={onBack}>
        Back
      </Button>
      <Button variant="primary" onClick={onSubmit} disabled={data.loading}>
        {data.loading ? (
          <>
            <Spinner
              animation="border"
              size="sm"
              className="me-2"
              role="status"
            />
            Submitting...
          </>
        ) : (
          "Submit"
        )}
      </Button>
    </div>
  );

  const SubmittedFooter = (
    <div className="d-flex justify-content-between mt-3">
      <Button variant="outline-secondary" onClick={onClose}>
        Close
      </Button>
      {printButton}
    </div>
  );

  const DetailsFooter = (
    <div className="d-flex justify-content-between mt-3">
      <Button variant="outline-secondary" onClick={onClose}>
        Close
      </Button>

      <div>
        {onApprove && (
          <>
            <Button
              className="me-2"
              variant="outline-success"
              onClick={() => onApprove(true)}
            >
              Approve
            </Button>
            <Button
              className="me-2"
              variant="outline-danger"
              onClick={() => onApprove(false)}
            >
              Reject
            </Button>
          </>
        )}
        {onEdit && (
          <Button className="me-2" variant="outline-danger" onClick={onEdit}>
            Edit
          </Button>
        )}
        {onDelete && (
          <Button className="me-2" variant="outline-danger" onClick={onDelete}>
            Delete
          </Button>
        )}
        {printButton}
      </div>
    </div>
  );

  const SubmittedSuccessMessage =
    mode === "submitted" && data.submitted ? (
      <center className="text-success fw-semibold my-2">
        Transaction recorded successfully
      </center>
    ) : null;

  const warningMessage =
    ["submitted", "details"].includes(mode) && data.status === "pending" ? (
      <center className="text-danger fw-semibold my-2">
        This transaction needs approval from head-office
      </center>
    ) : null;

  const ErrorMessage = data.error ? (
    <center className="text-danger fw-semibold my-2">{data.error}</center>
  ) : null;

  // RENDER ROOT
  return (
    <div>
      {/* PRINTABLE SECTION */}

      <div style={{ display: "none" }}>
        <div
          ref={componentRef}
          className="summary-print-container"
          style={{
            transform: `scale(${scale})`,
          }}
        >
          {renderHeader()}
          {printable}
        </div>
      </div>

      <div>
        {renderHeader()}
        {mode === "preview" ? renderPreviewContent?.(data) : summaryContent}
      </div>

      {SubmittedSuccessMessage}
      {warningMessage}
      {ErrorMessage}

      {/* FOOTER BUTTONS FOR EACH MODE */}
      {mode === "preview" && PreviewFooter}
      {mode === "submitted" && SubmittedFooter}
      {mode === "details" && DetailsFooter}
    </div>
  );
}
